import { useState, useEffect, useMemo } from 'react';
import { MdAdd, MdPerson, MdSchool, MdHistory, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { parentAPI } from '../../utils/api';
import { validateNewPassword, getPasswordError, checkPasswordRequirements } from '../../utils/validation';
import ManageSubjectsModal from './ManageSubjectsModal';
import ViewHistoryModal from './ViewHistoryModal';

const Plus = MdAdd as any;
const Person = MdPerson as any;
const School = MdSchool as any;
const History = MdHistory as any;
const VisibilityIcon = MdVisibility as any;
const VisibilityOffIcon = MdVisibilityOff as any;

interface Child {
  id: string;
  name: string;
  email: string;
  subjects: number;
  lessons: number;
}

interface ParentDashboardProps {
  onChildrenChange?: (hasChildren: boolean) => void;
}

export default function ParentDashboard({ onChildrenChange }: ParentDashboardProps) {
  const [children, setChildren] = useState<Child[]>([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChildForSubjects, setSelectedChildForSubjects] = useState<Child | null>(null);
  const [selectedChildForHistory, setSelectedChildForHistory] = useState<Child | null>(null);

  // Fetch children on mount
  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      setIsLoading(true);
      const response = await parentAPI.listChildren();
      setChildren(response.children);

      // Notify parent component about children count change
      if (onChildrenChange) {
        onChildrenChange(response.children.length > 0);
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-white text-lg">Loading children...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Children Overview */}
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h2 className="text-white text-xl sm:text-2xl font-bold">Manage Children</h2>
          <button
            data-tour="add-child-button"
            onClick={() => setShowAddChild(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors w-full sm:w-auto justify-center"
          >
            <Plus className="w-5 h-5" />
            Add Child
          </button>
        </div>

        {children.length === 0 ? (
          <div className="bg-[#1a1f2e] rounded-lg p-8 text-center border border-gray-700">
            <Person className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-white text-lg font-semibold mb-2">No Children Added</h3>
            <p className="text-gray-400 mb-4">
              Add child accounts to manage their learning experience and track their progress.
            </p>
            <button
              data-tour="add-child-button"
              onClick={() => setShowAddChild(true)}
              className="px-6 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
            >
              Add Your First Child
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {children.map((child) => (
              <ChildCard
                key={child.id}
                child={child}
                onManageSubjects={() => setSelectedChildForSubjects(child)}
                onViewHistory={() => setSelectedChildForHistory(child)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Child Modal */}
      {showAddChild && (
        <AddChildModal
          onClose={() => setShowAddChild(false)}
          onChildAdded={fetchChildren}
        />
      )}

      {/* Manage Subjects Modal */}
      {selectedChildForSubjects && (
        <ManageSubjectsModal
          child={selectedChildForSubjects}
          onClose={() => setSelectedChildForSubjects(null)}
          onSubjectAdded={fetchChildren}
        />
      )}

      {/* View History Modal */}
      {selectedChildForHistory && (
        <ViewHistoryModal
          child={selectedChildForHistory}
          onClose={() => setSelectedChildForHistory(null)}
        />
      )}
    </div>
  );
}

interface ChildCardProps {
  child: Child;
  onManageSubjects: () => void;
  onViewHistory: () => void;
}

function ChildCard({ child, onManageSubjects, onViewHistory }: ChildCardProps) {
  return (
    <div className="bg-[#1a1f2e] rounded-lg p-4 sm:p-6 border border-gray-700 hover:border-gray-600 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Person className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-white text-base sm:text-lg font-semibold truncate">{child.name}</h3>
            <p className="text-gray-400 text-xs sm:text-sm truncate">{child.email}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
          <button
            data-tour="manage-subjects-button"
            onClick={onManageSubjects}
            className="px-3 sm:px-4 py-2 bg-secondary text-white rounded hover:bg-secondary/90 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
          >
            <School className="w-4 h-4" />
            <span className="hidden sm:inline">Manage Subjects</span>
            <span className="sm:hidden">Manage Subjects</span>
          </button>
          <button
            data-tour="view-history-button"
            onClick={onViewHistory}
            className="px-3 sm:px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
          >
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">View History</span>
            <span className="sm:hidden">History</span>
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-6 text-sm">
        <div className="flex justify-between sm:block">
          <span className="text-gray-400">Subjects:</span>
          <span className="text-white sm:ml-2 font-semibold">{child.subjects}</span>
        </div>
        <div className="flex justify-between sm:block">
          <span className="text-gray-400">Lessons Completed:</span>
          <span className="text-white sm:ml-2 font-semibold">{child.lessons}</span>
        </div>
      </div>
    </div>
  );
}

interface AddChildModalProps {
  onClose: () => void;
  onChildAdded: () => void;
}

function AddChildModal({ onClose, onChildAdded }: AddChildModalProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState('');

  // Compute password requirements status
  const passwordRequirements = useMemo(() => checkPasswordRequirements(formData.password), [formData.password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPasswordError('');
    setIsSubmitting(true);

    // Validate password and confirm password
    const validation = validateNewPassword(formData.password, formData.confirmPassword);
    if (!validation.isValid) {
      setPasswordError(validation.error || 'Invalid password');
      setIsSubmitting(false);
      return;
    }

    try {
      await parentAPI.createChild(formData.fullName, formData.email, formData.password);
      onChildAdded(); // Refresh the children list
      onClose();
    } catch (err: any) {
      console.error('Failed to create child account:', err);
      setError(err.message || 'Failed to create child account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update password error on change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });

    // Show validation errors only if user has started typing in confirm password
    if (formData.confirmPassword) {
      const validation = validateNewPassword(newPassword, formData.confirmPassword);
      setPasswordError(validation.isValid ? '' : validation.error || '');
    } else if (newPassword) {
      // Show password requirements error if only password is filled
      const error = getPasswordError(newPassword);
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setFormData({ ...formData, confirmPassword: newConfirmPassword });

    // Validate on change
    if (formData.password && newConfirmPassword) {
      const validation = validateNewPassword(formData.password, newConfirmPassword);
      setPasswordError(validation.isValid ? '' : validation.error || '');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111827] rounded-lg p-4 sm:p-6 max-w-md w-full border border-gray-600">
        <h2 className="text-white text-lg sm:text-xl font-bold mb-4">Add Child Account</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Full Name</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 pr-10 bg-[#1a1f2e] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? (
                  <VisibilityOffIcon className="w-5 h-5" />
                ) : (
                  <VisibilityIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            <div className="mt-2 space-y-1">
              <p className={`text-xs flex items-center gap-1.5 ${passwordRequirements.minLength ? 'text-green-500' : 'text-gray-400'}`}>
                <span className="text-lg leading-none">{passwordRequirements.minLength ? '✓' : '○'}</span>
                At least 8 characters
              </p>
              <p className={`text-xs flex items-center gap-1.5 ${passwordRequirements.hasNumber ? 'text-green-500' : 'text-gray-400'}`}>
                <span className="text-lg leading-none">{passwordRequirements.hasNumber ? '✓' : '○'}</span>
                Contains a number
              </p>
              <p className={`text-xs flex items-center gap-1.5 ${passwordRequirements.hasSpecialChar ? 'text-green-500' : 'text-gray-400'}`}>
                <span className="text-lg leading-none">{passwordRequirements.hasSpecialChar ? '✓' : '○'}</span>
                Contains a special character (!@#$%^&*...)
              </p>
            </div>
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleConfirmPasswordChange}
                className="w-full px-3 py-2 pr-10 bg-[#1a1f2e] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showConfirmPassword ? (
                  <VisibilityOffIcon className="w-5 h-5" />
                ) : (
                  <VisibilityIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {(error || passwordError) && (
            <div className="bg-red-500/10 border border-red-500 rounded p-3 text-red-500 text-sm">
              {error || passwordError}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                'Add Child'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
